import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plus } from 'lucide-react';
import SizePopover from '../create-sales-order/SizePopover';
import MultiColorSizePopover from '../create-sales-order/MultiColorSizePopover';
import { rowTotalQty } from './helpers';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const InvoiceTable = ({
  rows, groupedBoxRows, hasMultipleBoxes, channel,
  products, brands,
  updateRow, selectDesign, removeRow, addRow, copyDiscountToAll,
  filteredDesigns, getBrandName, handleTableKeyDown,
  breakPopupIdx, setBreakPopupIdx, handleBreak,
  grandTotal, grandQty,
  // SO-style handlers
  toggleSize, applyBand, setSizeQty, setSizeRate, selectAllSizes, clearSizes,
  getSizesForColor, applyMultiColor, clearColorPicker, clearSizePicker,
  setMasterQty, onSizesDone, copyPrevSizes,
  bulkDiscount, setBulkDiscount,
}) => {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm" data-testid="se-invoice-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left w-12 font-semibold text-gray-600">Sr</th>
                <th className="px-3 py-2 text-left min-w-[200px] font-semibold text-gray-600">Design No</th>
                <th className="px-3 py-2 text-left min-w-[120px] font-semibold text-gray-600">Colour</th>
                <th className="px-3 py-2 text-left min-w-[150px] font-semibold text-gray-600">Sizes</th>
                <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Qty<div className="text-[10px] font-normal text-gray-400">total</div></th>
                <th className="px-3 py-2 text-right w-24 font-semibold text-gray-600">Rate/pc</th>
                <th className="px-3 py-2 text-right w-28 font-semibold text-gray-600">
                  <div>Discount %</div>
                  <Input type="number" min="0" max="100" step="0.5" value={bulkDiscount}
                    onChange={e => { setBulkDiscount(e.target.value); copyDiscountToAll(e.target.value); }}
                    placeholder="All" className="h-6 text-xs text-right w-16 ml-auto mt-0.5" onWheel={e => e.target.blur()}
                    data-testid="se-bulk-discount" />
                </th>
                <th className="px-3 py-2 text-right w-28 font-semibold text-gray-600">Amount</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedBoxRows).sort(([a],[b]) => Number(a) - Number(b)).map(([boxNum, custSections]) => (
                <React.Fragment key={`box-${boxNum}`}>
                  {hasMultipleBoxes && (
                    <tr className="bg-amber-100 border-b-2 border-amber-300">
                      <td colSpan="9" className="px-3 py-2 font-bold text-amber-800 text-sm">Box {boxNum}</td>
                    </tr>
                  )}
                  {Object.entries(custSections).map(([custId, { name, rows: sectionRows }]) => (
                    <React.Fragment key={`box-${boxNum}-${custId}`}>
                      {channel === 'via_distributor' && name && (
                        <tr className="bg-blue-50 border-b border-blue-200">
                          <td colSpan="9" className="px-4 py-1.5 text-xs font-semibold text-blue-700">
                            {name}
                            <span className="ml-2 text-gray-400 font-normal">({sectionRows.filter(r => r.product_id).length} items, {sectionRows.reduce((s, r) => s + rowTotalQty(r), 0)} pcs)</span>
                          </td>
                        </tr>
                      )}
                      {sectionRows.map((row) => {
                        const idx = row._idx;
                        const selSizes = Object.keys(row.sizeQty || {});
                        const rates = selSizes.map(s => (row.sizeRate || {})[s]);
                        const uniformRate = rates.length > 0 && rates.every(r => String(r) === String(rates[0]));
                        const mergedQty = rowTotalQty(row);
                        return (
                          <tr key={idx} className="border-b hover:bg-gray-50/50" data-testid={`se-row-${idx}`}>
                            <td className="px-1 py-1.5 text-gray-500 font-mono text-center">
                              <div className="flex items-center gap-0.5">
                                {rows.length > 1 && (
                                  <button onClick={() => removeRow(idx)} className="text-red-300 hover:text-red-500 p-0.5" title="Remove row" data-testid={`se-remove-row-${idx}`}>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                  </button>
                                )}
                                <span>{row.sr}</span>
                              </div>
                            </td>
                            {/* Design search */}
                            <td className="px-3 py-1.5 relative">
                              <Input value={row.designSearch}
                                onChange={e => { updateRow(idx, 'designSearch', e.target.value); updateRow(idx, 'showDesignDropdown', true); }}
                                onFocus={() => { if (row.designSearch) updateRow(idx, 'showDesignDropdown', true); }}
                                onBlur={() => {
                                  setTimeout(() => {
                                    const q = row.designSearch?.trim().toLowerCase();
                                    if (q && !row.design_code) {
                                      const exact = products.find(p => p.design_code.toLowerCase() === q);
                                      if (exact) selectDesign(idx, exact);
                                    }
                                    updateRow(idx, 'showDesignDropdown', false);
                                  }, 200);
                                }}
                                placeholder="Search design..." className="h-8 text-sm" data-testid={`se-design-search-${idx}`}
                                data-row={idx} data-col={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Tab' || e.key === 'Enter') {
                                    const q = row.designSearch?.trim().toLowerCase();
                                    if (q && !row.design_code) {
                                      const exact = products.find(p => p.design_code.toLowerCase() === q);
                                      if (exact) { e.preventDefault(); selectDesign(idx, exact); return; }
                                    }
                                  }
                                  handleTableKeyDown(e);
                                }}
                              />
                              {row.design_code && (
                                <div className="absolute right-4 top-1.5 flex items-center gap-1.5">
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{getBrandName(row.product?.brand_id)}</Badge>
                                  {(() => {
                                    const p = row.product;
                                    if (!p || !p.skus?.length) return <span className="w-2 h-2 rounded-full bg-red-400" title="No stock / Forwarding"></span>;
                                    const totalStock = p.skus.reduce((s, sk) => s + (sk.current_stock || 0), 0);
                                    const sizesWithStock = p.skus.filter(sk => (sk.current_stock || 0) > 0).length;
                                    const totalSizes = p.skus.length;
                                    if (sizesWithStock === totalSizes && totalStock > 0) return <span className="w-2 h-2 rounded-full bg-emerald-500" title={`In stock: ${totalStock} pcs`}></span>;
                                    if (sizesWithStock > 0) return <span className="w-2 h-2 rounded-full bg-amber-400" title={`Partial: ${sizesWithStock}/${totalSizes} sizes, ${totalStock} pcs`}></span>;
                                    return <span className="w-2 h-2 rounded-full bg-red-400" title="Out of stock"></span>;
                                  })()}
                                </div>
                              )}
                              {row.productMissing && (
                                <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600" data-testid={`se-design-missing-${idx}`}>
                                  <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                  <span>Design no longer in catalogue</span>
                                </div>
                              )}
                              {row.showDesignDropdown && row.designSearch && (
                                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto left-0">
                                  {filteredDesigns(row.designSearch).map(p => (
                                    <div key={p.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm flex items-center gap-2"
                                      onMouseDown={(e) => { e.preventDefault(); selectDesign(idx, p); }} data-testid={`se-design-option-${p.id}`}>
                                      {p.images?.[0] && <img src={`${API_BASE}${p.images[0]}`} alt="" className="w-8 h-8 rounded object-cover" />}
                                      <span className="font-mono font-semibold">{p.design_code}</span>
                                      <span className="text-gray-400 text-xs">{p.category}</span>
                                      <span className="text-gray-400 text-xs">{getBrandName(p.brand_id)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            {/* Colour: multi-colour + per-colour-size popover */}
                            <td className="px-3 py-1.5">
                              <MultiColorSizePopover idx={idx} row={row} sizesForColor={(color) => getSizesForColor(row.product, color)}
                                applyMultiColor={applyMultiColor} autoOpen={row.openColorPicker} onAutoOpenConsumed={clearColorPicker} />
                            </td>
                            {/* Sizes multi-select popover */}
                            <td className="px-3 py-1.5">
                              <SizePopover idx={idx} row={row}
                                toggleSize={toggleSize} applyBand={applyBand} setSizeQty={setSizeQty}
                                setSizeRate={setSizeRate} selectAllSizes={selectAllSizes} clearSizes={clearSizes}
                                setMasterQty={setMasterQty} autoOpen={row.openSizePicker} onAutoOpenConsumed={clearSizePicker}
                                onDone={onSizesDone} onOpenEmpty={copyPrevSizes} />
                            </td>
                            {/* Merged total qty (read-only) */}
                            <td className="px-3 py-1.5">
                              <Input disabled value={selSizes.length ? mergedQty : ''} readOnly
                                placeholder="-" className="h-8 text-right text-sm w-16 min-w-[3.5rem] bg-gray-50 font-semibold text-gray-700"
                                data-testid={`se-qty-${idx}`} />
                            </td>
                            {/* Rate */}
                            <td className="px-3 py-1.5">
                              {selSizes.length === 0 ? (
                                <Input disabled className="h-8 text-right text-sm" placeholder="-" />
                              ) : uniformRate ? (
                                <Input type="number" min="0" step="0.01" value={rates[0] ?? ''} onChange={e => updateRow(idx, 'rate', e.target.value)}
                                  className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`se-rate-${idx}`}
                                  data-row={idx} data-col={4} onKeyDown={handleTableKeyDown} />
                              ) : (
                                <div className="h-8 flex items-center justify-end text-xs text-amber-600 font-medium" data-testid={`se-rate-mixed-${idx}`}>Mixed</div>
                              )}
                            </td>
                            {/* Discount */}
                            <td className="px-3 py-1.5">
                              <Input type="number" min="0" max="100" step="0.5" value={row.discount}
                                onChange={e => updateRow(idx, 'discount', e.target.value)}
                                className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`se-discount-${idx}`}
                                data-row={idx} data-col={5} onKeyDown={handleTableKeyDown} />
                            </td>
                            {/* Amount */}
                            <td className="px-3 py-1.5 text-right font-semibold text-gray-900">
                              {row.amount > 0 ? `\u20B9${row.amount.toLocaleString('en-IN')}` : '-'}
                            </td>
                            {/* Break / actions */}
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-1">
                                <div className="relative">
                                  <button onClick={() => setBreakPopupIdx(breakPopupIdx === idx ? null : idx)} className="text-amber-500 hover:text-amber-700 text-xs font-semibold border border-amber-300 rounded px-2 py-0.5 hover:bg-amber-50" data-testid={`se-break-${idx}`}>Break</button>
                                  {breakPopupIdx === idx && (
                                    <div className="absolute right-0 top-7 z-50 bg-white border rounded-lg shadow-xl p-2 w-44">
                                      {channel === 'via_distributor' && (
                                        <button onClick={() => handleBreak('new_customer')} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded" data-testid="se-break-new-cust">Another Customer</button>
                                      )}
                                      <button onClick={() => handleBreak('new_box')} className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 rounded" data-testid="se-break-new-box">New Box</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2">
              <tr>
                <td colSpan="4" className="px-3 py-2"></td>
                <td className="px-3 py-2 text-right font-bold text-gray-700">{grandQty}</td>
                <td></td>
                <td></td>
                <td className="px-3 py-2 text-right font-bold text-gray-900 text-base">{'\u20B9'}{grandTotal.toLocaleString('en-IN')}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="border-t p-2 flex justify-start">
          <Button variant="ghost" size="sm" onClick={addRow} className="gap-1.5 text-green-700 hover:bg-green-50 text-xs" data-testid="se-add-row">
            <Plus className="w-3.5 h-3.5" /> Add design row
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceTable;
